/**
 * 로그인 사용자·테넌트별 테이블 컬럼 너비 스코프.
 * UnifiedLayout이 user로 Provider를 감싸면 TanStackTableWrapper가 자동으로 사용자별 저장을 사용합니다.
 */

import { createContext, useContext, useMemo, type ReactNode } from 'react';
import type { User } from '@shared/types';

export interface TableColumnPrefs {
  tenantId?: string;
  userId?: string;
}

const TableColumnPrefsContext = createContext<TableColumnPrefs>({});

export function resolveTableColumnPrefsFromUser(user?: User | null): TableColumnPrefs {
  if (!user) return {};
  if (user.userType === 'tenant-user') {
    return { tenantId: user.tenantId, userId: user.userId };
  }
  return { userId: user.adminUserId };
}

export function TableColumnPrefsProvider({
  tenantId,
  userId,
  children,
}: TableColumnPrefs & { children: ReactNode }) {
  const value = useMemo(
    () => ({
      tenantId: tenantId?.trim() || undefined,
      userId: userId?.trim() || undefined,
    }),
    [tenantId, userId],
  );
  return (
    <TableColumnPrefsContext.Provider value={value}>{children}</TableColumnPrefsContext.Provider>
  );
}

/** user가 있으면 테넌트·사용자 ID를 추출해 Provider로 감쌉니다. */
export function TableColumnPrefsFromUser({
  user,
  children,
}: {
  user?: User | null;
  children: ReactNode;
}) {
  const prefs = useMemo(() => resolveTableColumnPrefsFromUser(user), [user]);
  return <TableColumnPrefsProvider {...prefs}>{children}</TableColumnPrefsProvider>;
}

export function useTableColumnPrefs(): TableColumnPrefs {
  return useContext(TableColumnPrefsContext);
}
